import { useLocation, useNavigate, useParams } from "react-router-dom"
import { useState, useEffect, useRef } from "react"
import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Loader2,
  Video,
  Layout,
  AlertCircle,
  Users,
  FlaskConical,
} from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useTranslation } from "react-i18next"
import { UserMenu } from "@/features/auth/components/UserMenu"
import { LanguageSwitcher } from "@/components/common/LanguageSwitcher"
import { HistoryTab } from "@/features/history/components/HistoryTab"
import { TemplatesTab } from "@/features/templates/components/TemplatesTab"
import { CommunityTab } from "@/features/community/components/CommunityTab"
import { GeneratorSidebar } from "@/features/generator/components/GeneratorSidebar"
import { reportGenerateError } from "@/features/generator/lib/report-generate-error"
import { MobileSidebarDrawer } from "@/features/generator/components/MobileSidebarDrawer"
import { MobileHeader, WrksLink } from "@/components/layout/MobileHeader"
import { useGeneratorStore } from "@/stores/generatorStore"
import { useAuthStore } from "@/stores/authStore"
import { useHistory, useGenerateVideo } from "@/hooks/useHistory"
import { useAuthMe } from "@/hooks/useAuthMe"
import { useConfig } from "@/hooks/useConfig"
import { exitStaging } from "@/lib/staging"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { API_BASE_URL, APP_BASE_PATH } from "@/lib/constants"
import { isRedirectResponse } from "@/lib/api"
import { handOffToDeployment } from "@/lib/handoff"
import { AuthRequired } from "@/pages/AuthRequired"

function getTabFromPath(pathname: string): string | null {
  if (pathname === "/my") return "history"
  if (pathname === "/templates" || pathname.startsWith("/templates/")) return "templates"
  if (pathname === "/community") return "community"
  return null // "/" 경로는 기존 로직 사용
}

function getPathFromTab(tab: string): string {
  if (tab === "history") return "/my"
  if (tab === "community") return "/community"
  return "/templates"
}

export function Generator() {
  const { t } = useTranslation()
  const {
    prompt,
    ratio,
    resolution,
    duration,
    frameImages,
    selectedTemplate,
    isSubmitting,
    usageLimitModalOpen,
    ssoTokenExpiredModalOpen,
    setIsSubmitting,
    setUsageLimitModalOpen,
    setSsoTokenExpiredModalOpen,
  } = useGeneratorStore()

  const location = useLocation()
  const navigate = useNavigate()
  const { categoryId } = useParams<{ categoryId?: string }>()

  const [isScrolled, setIsScrolled] = useState(false)
  const [authDialogOpen, setAuthDialogOpen] = useState(false)
  const [isValidatingToken, setIsValidatingToken] = useState(true)
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const { isAuthenticated } = useAuthStore()
  const { data: historyData, isLoading: isHistoryLoading } = useHistory(1, 1)
  const { data: authMe, isError: authMeError } = useAuthMe()
  const { data: config } = useConfig()
  const communityHidden = authMe?.menuHidden.community ?? false
  const outageEnabled = config?.outageNotice?.enabled === true
  const outageMessage = config?.outageNotice?.message ?? ''

  // activeTab is DERIVED from the URL (the single source of truth) rather than mirrored in state via
  // effects — every tab switch navigates, so the path drives the active tab. "" = undetermined
  // (path "/" before the default is resolved) → renders the loading placeholder. When community is
  // hidden, a /community URL resolves to history while the redirect effect below navigates to /my.
  const urlTab = getTabFromPath(location.pathname)
  const activeTab = urlTab === "community" && communityHidden ? "history" : (urlTab ?? "")

  // Staging is admin-only. The routing cookie can be set manually, so any non-admin —
  // or a visitor whose /auth/me is rejected (the backend 403s non-admins under
  // STAGING_MODE) — is sent back to production by clearing the cookie.
  useEffect(() => {
    if (!config?.isStaging) return
    if (authMeError || (authMe && !authMe.isAdmin)) {
      exitStaging()
    }
  }, [config?.isStaging, authMeError, authMe])

  const generateMutation = useGenerateVideo()

  // 인증 검증: 토큰 유효성 확인 및 hasUsage 갱신
  useEffect(() => {
    const validateAuth = async () => {
      if (!isAuthenticated) {
        setIsValidatingToken(false)
        setAuthDialogOpen(true)
        return
      }

      try {
        const response = await fetch(`${API_BASE_URL}/auth/me`, {
          credentials: 'include',
        })
        if (!response.ok) {
          useAuthStore.getState().logout()
          setAuthDialogOpen(true)
        } else {
          const data = await response.json()
          // 잘못된 배포에 도달한 세션 → 백엔드 지시대로 올바른 도메인으로 이동.
          // 끝나지 않는 Promise 를 기다려 finally 가 검증 화면을 풀지 않게 한다.
          if (isRedirectResponse(data)) {
            return await handOffToDeployment(data.redirect)
          }
          // hasUsage 갱신 (Wrks API에서 최신 값 반영)
          const currentUser = useAuthStore.getState().user
          if (currentUser && data.hasUsage !== currentUser.hasUsage) {
            useAuthStore.getState().setUser({
              ...currentUser,
              hasUsage: data.hasUsage,
            })
          }
        }
      } catch {
        useAuthStore.getState().logout()
        setAuthDialogOpen(true)
      } finally {
        setIsValidatingToken(false)
      }
    }
    validateAuth()
  }, [isAuthenticated])

  // 탭 변경 핸들러 (URL도 함께 업데이트)
  const handleTabChange = (newTab: string) => {
    navigate(getPathFromTab(newTab))
  }

  // 카테고리 변경 핸들러 (URL 업데이트)
  const handleCategoryChange = (newCategoryId: string | null) => {
    if (newCategoryId) {
      navigate(`/templates/${newCategoryId}`)
    } else {
      navigate("/templates")
    }
  }

  // 최초 탭 결정: "/" 경로(탭 미지정)에서만 기본 탭으로 navigate. activeTab은 URL에서 파생되므로
  // 여기서 상태를 만지지 않고 URL만 바꾼다 (navigate 후 파생값이 자동 반영). URL에 탭이 있으면 즉시 사용.
  useEffect(() => {
    if (urlTab) return // URL에 이미 탭이 있으면 그대로 사용
    if (isHistoryLoading || !historyData) return // 기본값 결정에 히스토리 필요
    const defaultTab = historyData.items.length === 0 ? "templates" : "history"
    navigate(getPathFromTab(defaultTab), { replace: true }) // replace로 히스토리 스택에 추가 안 함
  }, [urlTab, isHistoryLoading, historyData, navigate])

  // 커뮤니티 메뉴가 숨김 상태인데 /community에 와 있으면 내 비디오로 리다이렉트 (URL만 변경)
  useEffect(() => {
    if (communityHidden && urlTab === "community") {
      navigate("/my", { replace: true })
    }
  }, [communityHidden, urlTab, navigate])

  const handleGenerate = async () => {
    if (!prompt.trim()) return
    if (isSubmitting) return

    // Build request payload
    const frameImagesPayload = [
      ...(frameImages.firstFrame ? [frameImages.firstFrame] : []),
      ...(frameImages.lastFrame ? [frameImages.lastFrame] : []),
    ]

    const requestBody: Record<string, unknown> = {
      prompt,
      ratio,
      resolution,
      duration,
      frameImages: frameImagesPayload.length > 0
        ? frameImagesPayload.map(img => ({
            role: img.role,
            s3Key: img.s3Key,
            thumbnailS3Key: img.thumbnailS3Key,
            filename: img.filename,
          }))
        : undefined,
    }

    if (selectedTemplate?.id) {
      requestBody.templateId = selectedTemplate.id
    }

    setIsSubmitting(true)

    try {
      await generateMutation.mutateAsync(requestBody)

      // 생성 시작 시 즉시 '내 비디오' 탭으로 전환 + 스크롤 맨 위로
      if (activeTab !== "history") {
        navigate("/my")
      }
      setTimeout(() => {
        scrollContainerRef.current?.scrollTo(0, 0)
      }, 0)
    } catch (error: unknown) {
      reportGenerateError(error, t)
    } finally {
      setIsSubmitting(false)
    }
  }

  // 토큰 검증 중이면 로딩 표시
  if (isValidatingToken) {
    return (
      <div className="flex h-screen bg-slate-50 items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    )
  }

  // 인증되지 않은 경우 AuthRequired 페이지 표시
  if (authDialogOpen) {
    return <AuthRequired />
  }

  return (
    <>
      <div className="flex flex-col lg:flex-row h-screen bg-slate-50 overflow-hidden">
        {/* Mobile Header - lg 미만에서만 표시 */}
        <MobileHeader
          onMenuClick={() => setMobileDrawerOpen(true)}
          className="lg:hidden"
        />

        {/* Mobile Sidebar Drawer */}
        <MobileSidebarDrawer
          open={mobileDrawerOpen}
          onOpenChange={setMobileDrawerOpen}
          onGenerate={handleGenerate}
        />

        {/* Desktop Sidebar - lg 이상에서만 표시 */}
        <GeneratorSidebar
          onGenerate={handleGenerate}
        />

        {/* Main Content - Tabs */}
        <main className="flex-1 flex flex-col overflow-hidden">
          {activeTab === "" ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : (
          <Tabs value={activeTab} onValueChange={handleTabChange} className="flex flex-col h-full">
            {/* Fixed Header - Tabs + Logout */}
            <div className={`h-14 lg:h-16 px-4 lg:px-8 shrink-0 bg-slate-50 flex items-center transition-shadow z-10 ${isScrolled ? 'shadow-sm' : ''}`}>
              <div className="w-full max-w-5xl mx-auto flex items-center justify-between">
                <TabsList className="w-full lg:w-auto">
                  <TabsTrigger value="history" className="flex-1 lg:flex-initial flex items-center justify-center gap-1.5 lg:gap-2 px-2 lg:px-6">
                    <Video className="w-4 h-4" />
                    <span className="hidden sm:inline">{t('pages.tabMyVideos')}</span>
                  </TabsTrigger>
                  <TabsTrigger value="templates" className="flex-1 lg:flex-initial flex items-center justify-center gap-1.5 lg:gap-2 px-2 lg:px-6">
                    <Layout className="w-4 h-4" />
                    <span className="hidden sm:inline">{t('pages.tabTemplates')}</span>
                  </TabsTrigger>
                  {!communityHidden && (
                    <TabsTrigger value="community" className="flex-1 lg:flex-initial flex items-center justify-center gap-1.5 lg:gap-2 px-2 lg:px-6">
                      <Users className="w-4 h-4" />
                      <span className="hidden sm:inline">{t('pages.tabCommunity')}</span>
                    </TabsTrigger>
                  )}
                </TabsList>
                {/* WrksLink + LanguageSwitcher + UserMenu - 데스크톱에서만 표시 (모바일은 MobileHeader에 있음) */}
                <div className="hidden lg:flex items-center gap-2">
                  <WrksLink />
                  <LanguageSwitcher />
                  <UserMenu />
                </div>
              </div>
            </div>

            {/* Staging banner */}
            {config?.isStaging && (
              <div className="px-4 lg:px-8 shrink-0 bg-slate-50">
                <div className="w-full max-w-5xl mx-auto pb-3">
                  <div className="flex items-center justify-center gap-2 rounded-md border border-amber-300 bg-amber-50 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-amber-700">
                    <FlaskConical className="h-3.5 w-3.5" />
                    {t('pages.stagingBanner')}
                  </div>
                </div>
              </div>
            )}

            {/* Outage Notice (always visible above tab content) */}
            {outageEnabled && (
              <div className="px-4 lg:px-8 shrink-0 bg-slate-50">
                <div className="w-full max-w-5xl mx-auto pb-3">
                  <Alert variant="destructive" className="border-red-200 bg-red-50">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="whitespace-pre-wrap">
                      {outageMessage}
                    </AlertDescription>
                  </Alert>
                </div>
              </div>
            )}

            {/* Scrollable Content */}
            <div
              ref={scrollContainerRef}
              className="flex-1 overflow-y-auto px-4 lg:px-8 pt-0 pb-6"
              onScroll={(e) => setIsScrolled(e.currentTarget.scrollTop > 0)}
            >
              <div className="w-full max-w-5xl mx-auto">
                <TabsContent value="history" className="mt-0">
                  <HistoryTab />
                </TabsContent>

                <TabsContent value="templates" className="mt-0">
                  <TemplatesTab
                    selectedCategoryId={categoryId ?? null}
                    onCategoryChange={handleCategoryChange}
                  />
                </TabsContent>

                {!communityHidden && (
                  <TabsContent value="community" className="mt-0">
                    <CommunityTab />
                  </TabsContent>
                )}
              </div>
            </div>
          </Tabs>
          )}
        </main>
      </div>

      {/* 사용량 한도 초과 모달 */}
      <Dialog open={usageLimitModalOpen} onOpenChange={setUsageLimitModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="space-y-3">
            <DialogTitle className="flex items-center gap-2 text-xl">
              <AlertCircle className="w-6 h-6 text-red-600" />
              {t('modals.usageLimitTitle')}
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground">
              {t('modals.usageLimitBody')}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="mt-4">
            <Button
              onClick={() => setUsageLimitModalOpen(false)}
              className="w-full sm:w-auto"
            >
              {t('common.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SSO 토큰 만료 모달 */}
      <Dialog open={ssoTokenExpiredModalOpen} onOpenChange={setSsoTokenExpiredModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="space-y-3">
            <DialogTitle className="flex items-center gap-2 text-xl">
              <AlertCircle className="w-6 h-6 text-amber-500" />
              {t('modals.ssoExpiredTitle')}
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground">
              {t('modals.ssoExpiredBody')}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="mt-4">
            <Button
              onClick={async () => {
                setSsoTokenExpiredModalOpen(false)
                await useAuthStore.getState().logout()
                window.location.href = APP_BASE_PATH
              }}
              className="w-full sm:w-auto"
            >
              {t('common.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
